import { useNavigate } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import {
    MERCHANT_ADMIN_CREATE_PRICE_LIST_ROUTE,
    MERCHANT_ADMIN_PRICE_LISTS_ROUTE,
    MERCHANT_ADMIN_PRODUCTS_LIST_ROUTE,
    MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE,
    MERCHANT_ADMIN_ROUTE_DEVICES,
    MERCHANT_ADMIN_SURVEYS,
    MERCHANT_NEWS_ROUTE,
    MERCHANT_ADMIN_CREATE_ROUTE,
    MERCHANT_ADMIN_USERS_ROUTE,
    MERCHANT_ADMIN_SHOPS_LIST_ROUTE,
    MERCHANT_ADMIN_SHOPS_LIST_CREATE_ROUTE,
    MERCHANT_ADMIN_PRODUCTS_CREATE_ROUTE,
    MERCHANT_ADMIN_PRODUCTS_GROUP_CREATE_ROUTE,
    MERCHANT_ADMIN_SETTINGS_VAT_CODES_ROUTE,
    MERCHANT_ADMIN_SETTINGS_CREATE_VAT_CODES_ROUTE,
    MERCHANT_ADMIN_MERCHANT_ACCOUNT_ROUTE,
    MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATIONS_ROUTE,
    MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATION_CREATE_ROUTE,
    MERCHANT_ADMIN_OFFERS_ROUTE,
    MERCHANT_ADMIN_CREATE_SURVEY_ITEM,
    MERCHANT_NEWS_CREATE_NEWS_ITEM_ROUTE,
    MERCHANT_ADMIN_SETTINGS_ID_CARDS_ROUTE,
    MERCHANT_ADMIN_SHOPS_CUSTOMER_ACCESS_ROUTE,
    MERCHANT_ADMIN_PAYMENT_LINK_CREATE_ROUTE,
    MERCHANT_ADMIN_PAYMENT_LINKS_ROUTE,
    MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNTS_ROUTE,
    MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNT_CREATE_ROUTE,
} from "../../../../constants/routes"
import { MatchEnvironment } from "../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../environment/envTypes"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { AdminHomeCard, AdminHomeCardGroup, AdminHomeCardGroups } from "../../../admin/components/AdminHomeCard"

const useStyles = makeStyles()((_theme) => ({
    page: {
        paddingTop: "3rem",
    },
}))

type Props = Readonly<{}>

export function MerchantAdminHomePage({}: Props) {
    const { classes } = useStyles()
    const navigate = useNavigate()

    return (
        <div className={classes.page}>
            <AdminHomeCardGroups>
                <LocalizedStrict id="merchant-admin-home-communication-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Communication">
                        <LocalizedStrict
                            id="merchant-admin-home-merchant-surveys"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="Surveys"
                                description="Monitor your customer satisfaction by creating and managing surveys."
                                onCreateClick={() => navigate(MERCHANT_ADMIN_CREATE_SURVEY_ITEM)}
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_SURVEYS)}
                            />
                        </LocalizedStrict>
                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.QA]}>
                            <LocalizedStrict
                                id="merchant-admin-home-merchant-news-card"
                                attrs={{ title: true, description: true }}
                            >
                                <AdminHomeCard
                                    title="Merchant news"
                                    description="Manage your news to your customers"
                                    onCreateClick={() => navigate(MERCHANT_NEWS_CREATE_NEWS_ITEM_ROUTE)}
                                    onShowAllClick={() => navigate(MERCHANT_NEWS_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>
                    </AdminHomeCardGroup>
                </LocalizedStrict>

                <LocalizedStrict id="merchant-admin-home-products-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Products">
                        <LocalizedStrict id="merchant-admin-home-products" attrs={{ title: true, description: true }}>
                            <AdminHomeCard
                                onCreateClick={() => navigate(MERCHANT_ADMIN_PRODUCTS_CREATE_ROUTE)}
                                title="Products"
                                description="Add and maintain products for your shops"
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_PRODUCTS_LIST_ROUTE)}
                            />
                        </LocalizedStrict>

                        <LocalizedStrict
                            id="merchant-admin-home-product-groups"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                onCreateClick={() => navigate(MERCHANT_ADMIN_PRODUCTS_GROUP_CREATE_ROUTE)}
                                title="Product Groups"
                                description="Group your products and control the visibility of product groups throughout the day"
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE)}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-admin-home-price-list" attrs={{ title: true, description: true }}>
                            <AdminHomeCard
                                onCreateClick={() => navigate(MERCHANT_ADMIN_CREATE_PRICE_LIST_ROUTE)}
                                title="Price Lists"
                                description="You can use price lists to offer selections of products at differentiated prices to specified customers"
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_PRICE_LISTS_ROUTE)}
                            />
                        </LocalizedStrict>
                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.QA]}>
                            <LocalizedStrict
                                id="merchant-admin-home-offers-card"
                                attrs={{ title: true, description: true }}
                            >
                                <AdminHomeCard
                                    onCreateClick={() => {}}
                                    title="Offers"
                                    description="Manage your offers"
                                    onShowAllClick={() => navigate(MERCHANT_ADMIN_OFFERS_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>
                    </AdminHomeCardGroup>
                </LocalizedStrict>

                <LocalizedStrict id="merchant-admin-home-shop" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Shops">
                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.QA]}>
                            <LocalizedStrict id="merchant-admin-home-shop" attrs={{ title: true, description: true }}>
                                <AdminHomeCard
                                    onCreateClick={() => navigate(MERCHANT_ADMIN_SHOPS_LIST_CREATE_ROUTE)}
                                    title="Shops"
                                    description="Sell your products through shops with individualized branding"
                                    onShowAllClick={() => navigate(MERCHANT_ADMIN_SHOPS_LIST_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>

                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.QA]}>
                            <LocalizedStrict
                                id="merchant-admin-home-payment-links"
                                attrs={{ title: true, description: true }}
                            >
                                <AdminHomeCard
                                    onCreateClick={() => navigate(MERCHANT_ADMIN_PAYMENT_LINK_CREATE_ROUTE)}
                                    title="Payment links"
                                    description="Create a unique payment link for guest or course participants."
                                    onShowAllClick={() => navigate(MERCHANT_ADMIN_PAYMENT_LINKS_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>

                        <LocalizedStrict
                            id="merchant-admin-home-devices-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="Devices"
                                description="Show devices, like screens, printers, credit card terminals."
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_ROUTE_DEVICES)}
                            />
                        </LocalizedStrict>

                        <LocalizedStrict
                            id="merchant-admin-home-customers-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="Customers access"
                                description="Manage customer access to your merchant"
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_SHOPS_CUSTOMER_ACCESS_ROUTE)}
                            />
                        </LocalizedStrict>
                    </AdminHomeCardGroup>
                </LocalizedStrict>

                <LocalizedStrict id="merchant-admin-home-settings-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Settings">
                        <LocalizedStrict
                            id="merchant-admin-home-vat-codes-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                onCreateClick={() => navigate(MERCHANT_ADMIN_SETTINGS_CREATE_VAT_CODES_ROUTE)}
                                title="VAT Codes"
                                description="Manage VAT codes"
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_SETTINGS_VAT_CODES_ROUTE)}
                            />
                        </LocalizedStrict>

                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.QA]}>
                            <LocalizedStrict
                                id="merchant-admin-home-delivery-locations-card"
                                attrs={{ title: true, description: true }}
                            >
                                <AdminHomeCard
                                    onCreateClick={() =>
                                        navigate(MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATION_CREATE_ROUTE)
                                    }
                                    title="Delivery locations"
                                    description="Manage delivery locations where customers can pick up orders."
                                    onShowAllClick={() => navigate(MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATIONS_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>
                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.QA]}>
                            <LocalizedStrict
                                id="merchant-admin-home-sales-accounts-card"
                                attrs={{ title: true, description: true }}
                            >
                                <AdminHomeCard
                                    onCreateClick={() => navigate(MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNT_CREATE_ROUTE)}
                                    title="Sales accounts"
                                    description="Create sales accounts for posting sales."
                                    onShowAllClick={() => navigate(MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNTS_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>
                        <LocalizedStrict
                            id="merchant-admin-home-id-cards-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="ID cards"
                                description="View your customers ID cards used for POS payments."
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_SETTINGS_ID_CARDS_ROUTE)}
                            />
                        </LocalizedStrict>
                    </AdminHomeCardGroup>
                </LocalizedStrict>

                <LocalizedStrict id="merchant-admin-home-account-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Account">
                        <LocalizedStrict id="merchant-admin-home-admin-card" attrs={{ title: true, description: true }}>
                            <AdminHomeCard
                                title="Administrators"
                                description="Manage administrators for your merchant"
                                onShowAllClick={() => navigate(MERCHANT_ADMIN_USERS_ROUTE)}
                                onCreateClick={() => navigate(MERCHANT_ADMIN_CREATE_ROUTE)}
                            />
                        </LocalizedStrict>
                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.QA]}>
                            <LocalizedStrict
                                id="merchant-admin-home-merchant-account-card"
                                attrs={{ title: true, description: true, buttonText: true }}
                            >
                                <AdminHomeCard
                                    title="Merchant Account"
                                    buttonText="Show"
                                    description="Update your merchant account details"
                                    onShowAllClick={() => navigate(MERCHANT_ADMIN_MERCHANT_ACCOUNT_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>
                    </AdminHomeCardGroup>
                </LocalizedStrict>
            </AdminHomeCardGroups>
        </div>
    )
}
